import React, { useEffect } from 'react';
import Showcase from './showcase';
import { useAffinityShowcase } from '../hooks/useAffinityShowcase';

interface AffinityShowcaseProps {
  /** The listing on screen: never shown in its own recommendations. */
  listingId: number;
  /** This listing's own tags, for a buyer with no history to go on. */
  listingTags?: string[];
  setProduct?: (product: Record<string, unknown>) => void;
  tag?: string;
  /** Called once it is certain there is nothing to show. */
  onEmpty?: () => void;
}

/**
 * The row that stands in for "Other items from {seller}" when the seller has
 * too few other listings to make one (components/product-detail).
 *
 * First choice is the buyer's own strongest interest; a buyer with no history
 * yet gets more of what this listing is tagged with. When neither has enough
 * to show, it renders nothing and says so through `onEmpty`, so the page can
 * drop the ad that would otherwise sit directly under another ad.
 */
/**
 * Sizes and one-letter codes are tags too ("M", "XL", "10"), and "More M" is
 * not a row anyone wants -- staging showed exactly that on 2026-09-24.
 */
const SIZE_TAG = /^(x{0,3}s|s|m|l|x{0,3}l|\d+(\.\d+)?)$/i;

/** The first of this listing's tags that names a thing, not a size. */
export function pickListingTag(tags?: string[]): string | undefined {
  return tags?.map((t) => t.trim()).find((t) => t.length >= 3 && !SIZE_TAG.test(t));
}

/** Renders nothing; tells the page the slot stayed empty. */
const ReportEmpty = ({ onEmpty }: { onEmpty?: () => void }) => {
  useEffect(() => {
    onEmpty?.();
  }, [onEmpty]);
  return null;
};

const AffinityShowcase = ({ listingId, listingTags, setProduct, tag, onEmpty }: AffinityShowcaseProps) => {
  const { showcase, loaded } = useAffinityShowcase(true, listingId);
  const listingTag = pickListingTag(listingTags);

  const nothingToShow = loaded && !showcase && !listingTag;
  useEffect(() => {
    if (nothingToShow) onEmpty?.();
  }, [nothingToShow, onEmpty]);

  if (!loaded) return null;

  if (showcase) {
    return (
      <Showcase
        key={`affinity-${showcase.tag}`}
        title={showcase.title || showcase.tag}
        searchParams={{ tags: [showcase.tag], hasPhoto: true }}
        initialListings={showcase.listings as Record<string, unknown>[]}
        listName="affinityList"
        setProduct={setProduct}
        tag={tag}
        itemsPerPage={10}
        excludeListingId={listingId}
        // Lazy for the LAYOUT, not the fetch (the listings are already here):
        // it is the branch that indents the heading and keeps the card margin,
        // matching the seller row and "Similar Listings" around it.
        lazy={true}
        noMargin={false}
        recommendForever
      />
    );
  }

  if (listingTag) {
    return (
      <Showcase
        key={`listing-tag-${listingTag}`}
        minShown={4}
        fallback={<ReportEmpty onEmpty={onEmpty} />}
        title={`More ${listingTag}`}
        searchParams={{ tags: [listingTag], hasPhoto: true }}
        listName="affinityList"
        setProduct={setProduct}
        tag={tag}
        itemsPerPage={10}
        excludeListingId={listingId}
        lazy={true}
        noMargin={false}
        recommendForever
      />
    );
  }

  return null;
};

export default AffinityShowcase;
